import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Fingerprint, PlayCircle, X, Infinity as InfinityIcon, RefreshCw, Activity, Triangle, Square, Target, ArrowRight } from 'lucide-react';
import { useClickSound } from '../hooks/useClickSound';
interface SomaticTrackingProps {
  onComplete: () => void;
  onClose?: () => void;
  isStandalone?: boolean;
  initialVariant?: VariantType;
}
type VariantType = 'infinity' | 'circles' | 'wave' | 'triangle' | 'square' | 'spiral';
interface VariantData {
  title: string;
  subtitle: string;
  desc: string;
  theory: string;
  maxT: number;
  speed: number;
}
const VARIANTS: Record<VariantType, VariantData> = {
  infinity: {
    title: 'Synchronizacja Półkul',
    subtitle: 'Śledzenie Nieskończoności',
    desc: 'Płynny ruch obustronny zmusza prawą i lewą półkulę do komunikacji. Skutecznie obniża ładunek emocjonalny lęku i resetuje gonitwę myśli.',
    theory: 'Terapia EMDR, czyli odwrażliwianie za pomocą płynnego ruchu obustronnego. Kształt nieskończoności zmusza prawą i lewą półkulę mózgową do naprzemiennej pracy i intensywnej wymiany informacji. Dzięki temu zabiegowi, ładunek emocjonalny związany z lękiem lub stresem zostaje drastycznie obniżony, a gonitwa myśli ulega zresetowaniu.',
    maxT: Math.PI * 6,
    speed: 0.015
  },
  circles: {
    title: 'Odblokowanie Przepływu',
    subtitle: 'Cykliczne Okręgi (3 razy w prawo, potem 3 w lewo)',
    desc: 'Ruch obrotowy odblokowuje zastój energii (prany) w Splocie Słonecznym. Zmiana kierunku wyrównuje ciśnienie układu nerwowego.',
    theory: 'Ruch obrotowy i cykliczność bezpośrednio odblokowują zastój energii życiowej, tak zwanej prany, w obszarze Splotu Słonecznego. Wykonanie trzech okrążeń w prawo inicjuje przepływ i buduje dynamikę, a nagła zmiana kierunku na trzy okrążenia w lewo wyrównuje ciśnienie w układzie nerwowym, przywracając naturalny balans organizmu.',
    maxT: Math.PI * 12,
    speed: 0.02
  },
  wave: {
    title: 'Regulacja Nerwu Błędnego',
    subtitle: 'Fale Somatyczne',
    desc: 'Wizualizacja idealnego rytmu oddechu. Wznoszenie to wdech, opadanie to wydech. Naturalnie stymuluje odpoczynek przywspółczulny.',
    theory: 'Krzywa sinusoidalna to wizualizacja idealnego, zbalansowanego rytmu oddechu. Wznoszenie się fali symbolizuje powolny wdech, a jej opadanie - długi wydech. Takie wizualne prowadzenie naturalnie stymuluje nerw błędny i aktywuje przywspółczulny układ nerwowy, bezpośrednio odpowiedzialny za Twój głęboki odpoczynek i regenerację.',
    maxT: Math.PI * 8,
    speed: 0.012
  },
  triangle: {
    title: 'Ekstremalne Skupienie',
    subtitle: 'Geometria Ugruntowania',
    desc: 'Kiedy umysł tonie w chaosie (lęk/panika), geometria ściąga myśli do jednego przewidywalnego punktu (Point of Focus), silnie uziemiając.',
    theory: 'Kiedy umysł tonie w chaosie, lęku lub zbliżającym się ataku paniki, ostra geometria ściąga rozbiegane myśli do jednego, wysoce przewidywalnego punktu. Trójkąt, jako stabilna struktura oparta na szerokiej podstawie, wymaga większego wysiłku przy śledzeniu i działa jak psychologiczna kotwica, silnie uziemiając i przywracając poczucie kontroli nad ciałem.',
    maxT: 300,
    speed: 0.3
  },
  square: {
    title: 'Przewidywalność',
    subtitle: 'Kwadrat Bezpieczeństwa',
    desc: 'Równomierne, proste kąty przypominają układowi nerwowemu o istnieniu bezpiecznych granic. Idealne narzędzie do powstrzymania ataku paniki.',
    theory: 'Kąty proste rzadko występują w naturze. W psychologii kształtów reprezentują ludzką strukturę, porządek i nienaruszalne zasady. Prowadzenie palca po idealnie równych, symetrycznych odcinkach daje układowi nerwowemu jasny i mocny komunikat o istnieniu bezpiecznych granic, skutecznie powstrzymując narastającą panikę i uczucie przytłoczenia.',
    maxT: 400,
    speed: 0.4
  },
  spiral: {
    title: 'Głębokie Centrowanie',
    subtitle: 'Złota Spirala',
    desc: 'Śledzenie ruchu skierowanego do wewnątrz fizycznie ściąga Twoją rozproszoną uwagę z otoczenia, koncentrując ją w jednym, spokojnym rdzeniu.',
    theory: 'Spirala oparta na złotym podziale to jeden z najpotężniejszych i najstarszych wzorców występujących w naturze. Śledzenie ruchu nieustannie skierowanego do wewnątrz fizycznie ściąga Twoją rozproszoną uwagę z bodźców zewnętrznych, stopniowo koncentrując ją w jednym, bardzo spokojnym rdzeniu. To idealne narzędzie uwalniające od przebodźcowania.',
    maxT: Math.PI * 8,
    speed: 0.025
  }
};
const END_TEXTS = ['Twoje myśli odzyskały jasność.', 'Czujesz głęboki spokój.', 'Napięcie opuściło Twoje ciało.', 'Wracasz do swojego centrum.', 'Jesteś tu i teraz.', 'Twoja energia płynie swobodnie.', 'Odzyskujesz wewnętrzną równowagę.', 'Każdy oddech przynosi ulgę.', 'Twój umysł jest jak spokojna tafla wody.', 'Jesteś w pełni bezpieczny.', 'Harmonia wypełnia Twoje ciało.', 'Chaos ustąpił miejsca przejrzystości.'];
export function SomaticTracking({
  onComplete,
  onClose,
  isStandalone = false,
  initialVariant = 'infinity'
}: SomaticTrackingProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {
    playClick,
    playSuccess
  } = useClickSound();
  const [currentVariant, setCurrentVariant] = useState<VariantType>(initialVariant);
  const [isTheoryOpen, setIsTheoryOpen] = useState(false);
  const [isReadingTheory, setIsReadingTheory] = useState(false);
  const [phase, setPhase] = useState<'tracing' | 'straightening' | 'done'>('tracing');
  const phaseRef = useRef<'tracing' | 'straightening' | 'done'>('tracing');
  const [endText, setEndText] = useState('');
  const [showInstruction, setShowInstruction] = useState(true);
  // Helper to update both phase state and ref
  const updatePhase = useCallback((newPhase: 'tracing' | 'straightening' | 'done') => {
    phaseRef.current = newPhase;
    setPhase(newPhase);
  }, []);
  // Game State Refs (to avoid constant re-renders during animation)
  const stateRef = useRef({
    progressT: 0,
    pointerX: -1000,
    pointerY: -1000,
    lastPointerX: -1000,
    lastPointerY: -1000,
    isDragging: false,
    isWrongDirection: false,
    lastVibrateTime: 0,
    lastLap: 0,
    straightenT: 0,
    dotX: 0,
    dotY: 0,
    trail: [] as {
      t: number;
      age: number;
      isWrong: boolean;
    }[],
    lastTrailT: -1,
    audioCtx: null as AudioContext | null,
    humOscillator: null as OscillatorNode | null,
    humGain: null as GainNode | null,
    animationFrameId: 0
  });
  // --- Audio Engine ---
  const initAudio = useCallback(() => {
    const state = stateRef.current;
    if (!state.audioCtx) {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      state.audioCtx = new AudioContext();
    }
    if (state.audioCtx.state === 'suspended') state.audioCtx.resume();
    if (!state.humOscillator && state.audioCtx) {
      state.humOscillator = state.audioCtx.createOscillator();
      state.humOscillator.type = 'triangle';
      state.humOscillator.frequency.value = 120;
      state.humGain = state.audioCtx.createGain();
      state.humGain.gain.value = 0;
      state.humOscillator.connect(state.humGain);
      state.humGain.connect(state.audioCtx.destination);
      state.humOscillator.start();
    }
  }, []);
  const setHumActive = useCallback((isActive: boolean) => {
    const state = stateRef.current;
    if (!state.humGain || !state.audioCtx) return;
    const now = state.audioCtx.currentTime;
    if (isActive) {
      state.humGain.gain.setTargetAtTime(0.05, now, 0.1);
    } else {
      state.humGain.gain.setTargetAtTime(0, now, 0.2);
    }
  }, []);
  const playLapSound = useCallback(() => {
    const state = stateRef.current;
    if (!state.audioCtx) return;
    const now = state.audioCtx.currentTime;
    const osc = state.audioCtx.createOscillator();
    const gain = state.audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(528, now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.15, now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 2);
    osc.connect(gain);
    gain.connect(state.audioCtx.destination);
    osc.start(now);
    osc.stop(now + 2);
  }, []);
  const playStraightenSound = useCallback(() => {
    const state = stateRef.current;
    if (!state.audioCtx) return;
    const now = state.audioCtx.currentTime;
    [396, 495, 594].forEach((freq, idx) => {
      const osc = state.audioCtx.createOscillator();
      const gain = state.audioCtx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now);
      const startTime = now + idx * 0.05;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.2, startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 3);
      osc.connect(gain);
      gain.connect(state.audioCtx.destination);
      osc.start(startTime);
      osc.stop(startTime + 3);
    });
  }, []);
  // --- Path Math ---
  const getPathPoint = useCallback((type: VariantType, t: number, width: number, height: number) => {
    const cx = width / 2;
    const cy = height / 2 + 30;
    let x = 0,
      y = 0;
    const scale = Math.min(width, height) * 0.35;
    if (type === 'infinity') {
      x = cx + scale * Math.cos(t) / (1 + Math.pow(Math.sin(t), 2));
      y = cy + scale * Math.sin(t) * Math.cos(t) / (1 + Math.pow(Math.sin(t), 2));
    } else if (type === 'circles') {
      let angle;
      if (t <= Math.PI * 6) {
        angle = t - Math.PI / 2;
      } else {
        const overT = t - Math.PI * 6;
        angle = Math.PI * 6 - overT - Math.PI / 2;
      }
      x = cx + scale * 0.8 * Math.cos(angle);
      y = cy + scale * 0.8 * Math.sin(angle);
    } else if (type === 'wave') {
      const totalW = width * 0.8;
      const startX = cx - totalW / 2;
      const mappedX = t / VARIANTS['wave'].maxT * totalW;
      x = startX + mappedX;
      y = cy + Math.sin(t) * (scale * 0.6);
    } else if (type === 'triangle') {
      const R = scale * 0.9;
      const p1 = {
        x: cx,
        y: cy - R
      };
      const p2 = {
        x: cx + R * 0.866,
        y: cy + R * 0.5
      };
      const p3 = {
        x: cx - R * 0.866,
        y: cy + R * 0.5
      };
      const localT = t % 100;
      const segment = Math.floor(t / 100) % 3;
      const progress = localT / 100;
      if (segment === 0) {
        x = p1.x + (p2.x - p1.x) * progress;
        y = p1.y + (p2.y - p1.y) * progress;
      } else if (segment === 1) {
        x = p2.x + (p3.x - p2.x) * progress;
        y = p2.y + (p3.y - p2.y) * progress;
      } else {
        x = p3.x + (p1.x - p3.x) * progress;
        y = p3.y + (p1.y - p3.y) * progress;
      }
    } else if (type === 'square') {
      const R = scale * 0.8;
      const p1 = {
        x: cx - R,
        y: cy - R
      };
      const p2 = {
        x: cx + R,
        y: cy - R
      };
      const p3 = {
        x: cx + R,
        y: cy + R
      };
      const p4 = {
        x: cx - R,
        y: cy + R
      };
      const localT = t % 100;
      const segment = Math.floor(t / 100) % 4;
      const progress = localT / 100;
      if (segment === 0) {
        x = p1.x + (p2.x - p1.x) * progress;
        y = p1.y + (p2.y - p1.y) * progress;
      } else if (segment === 1) {
        x = p2.x + (p3.x - p2.x) * progress;
        y = p2.y + (p3.y - p2.y) * progress;
      } else if (segment === 2) {
        x = p3.x + (p4.x - p3.x) * progress;
        y = p3.y + (p4.y - p3.y) * progress;
      } else {
        x = p4.x + (p1.x - p4.x) * progress;
        y = p4.y + (p1.y - p4.y) * progress;
      }
    } else if (type === 'spiral') {
      const R = scale * 0.9;
      const maxT = VARIANTS['spiral'].maxT;
      const progress = t / maxT;
      const r = R * (1 - progress);
      x = cx + r * Math.cos(t);
      y = cy + r * Math.sin(t);
    }
    return {
      x,
      y
    };
  }, []);
  const getInterpolatedPoint = useCallback((type: VariantType, t: number, animProgress: number, width: number, height: number) => {
    const pOrig = getPathPoint(type, t, width, height);
    const cx = width / 2;
    const cy = height / 2 + 30;
    const lineLen = Math.min(width, height) * 0.8;
    const startX = cx - lineLen / 2;
    const targetX = startX + t / VARIANTS[type].maxT * lineLen;
    const targetY = cy;
    const ease = 1 - Math.pow(1 - animProgress, 3);
    return {
      x: pOrig.x + (targetX - pOrig.x) * ease,
      y: pOrig.y + (targetY - pOrig.y) * ease
    };
  }, [getPathPoint]);
  const drawInterpolatedPath = useCallback((ctx: CanvasRenderingContext2D, type: VariantType, animProgress: number, width: number, height: number) => {
    ctx.beginPath();
    ctx.strokeStyle = animProgress > 0 ? `rgba(244, 114, 182, ${0.4 + animProgress * 0.6})` : 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 4;
    ctx.lineJoin = 'round';
    const max = VARIANTS[type].maxT;
    let steps = 200;
    if (type === 'triangle') steps = 300;
    if (type === 'square') steps = 400;
    const step = max / steps;
    const p0 = getInterpolatedPoint(type, 0, animProgress, width, height);
    ctx.moveTo(p0.x, p0.y);
    for (let i = 0; i <= max; i += step) {
      const p = getInterpolatedPoint(type, i, animProgress, width, height);
      ctx.lineTo(p.x, p.y);
    }
    const pEnd = getInterpolatedPoint(type, max, animProgress, width, height);
    ctx.lineTo(pEnd.x, pEnd.y);
    ctx.stroke();
  }, [getInterpolatedPoint]);
  // --- Game Loop ---
  const gameLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const state = stateRef.current;
    const width = canvas.width;
    const height = canvas.height;
    const cx = width / 2;
    const cy = height / 2 + 30;
    const currentPhase = phaseRef.current;
    ctx.clearRect(0, 0, width, height);
    if (currentPhase === 'tracing') {
      drawInterpolatedPath(ctx, currentVariant, 0, width, height);
      if (currentVariant === 'circles') {
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.font = '40px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const directionIcon = state.progressT < Math.PI * 6 ? '↻' : '↺';
        ctx.fillText(directionIcon, cx, cy);
      }
      const currentPoint = getPathPoint(currentVariant, state.progressT, width, height);
      state.dotX = currentPoint.x;
      state.dotY = currentPoint.y;
      // Record trail using t-values for accurate path following
      if (state.isDragging) {
        const vData = VARIANTS[currentVariant];
        const tStep = vData.maxT / 600;
        if (state.lastTrailT < 0) {
          state.trail.push({
            t: state.progressT,
            age: 1.0,
            isWrong: state.isWrongDirection
          });
          state.lastTrailT = state.progressT;
        } else {
          const diff = state.progressT - state.lastTrailT;
          if (Math.abs(diff) > tStep) {
            const steps = Math.max(1, Math.ceil(Math.abs(diff) / tStep));
            for (let s = 1; s <= steps; s++) {
              const interpT = state.lastTrailT + diff / steps * s;
              const clampedT = Math.max(0, Math.min(interpT, vData.maxT));
              state.trail.push({
                t: clampedT,
                age: 1.0,
                isWrong: state.isWrongDirection
              });
            }
            state.lastTrailT = state.progressT;
          }
        }
      }
      // Draw trail using path-computed positions
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (let i = 0; i < state.trail.length - 1; i++) {
        const p1 = state.trail[i];
        const p2 = state.trail[i + 1];
        const p1pos = getPathPoint(currentVariant, p1.t, width, height);
        const p2pos = getPathPoint(currentVariant, p2.t, width, height);
        ctx.beginPath();
        ctx.moveTo(p1pos.x, p1pos.y);
        ctx.lineTo(p2pos.x, p2pos.y);
        if (p1.isWrong) {
          ctx.strokeStyle = `rgba(239, 68, 68, ${p1.age * 0.8})`;
        } else {
          ctx.strokeStyle = `rgba(244, 114, 182, ${p1.age * 0.8})`;
        }
        ctx.lineWidth = p1.age * 12;
        ctx.stroke();
        state.trail[i].age -= 0.02;
      }
      state.trail = state.trail.filter(p => p.age > 0);
      const baseRadius = currentVariant === 'triangle' || currentVariant === 'square' ? 22 : 14;
      const dragRadius = currentVariant === 'triangle' || currentVariant === 'square' ? 28 : 18;
      ctx.beginPath();
      ctx.arc(state.dotX, state.dotY, state.isDragging ? dragRadius : baseRadius, 0, Math.PI * 2);
      if (state.isWrongDirection) {
        ctx.fillStyle = 'rgba(239, 68, 68, 0.8)';
        ctx.shadowColor = '#ef4444';
      } else {
        ctx.fillStyle = state.isDragging ? '#fdf2f8' : 'rgba(244, 114, 182, 0.4)';
        ctx.shadowColor = '#f472b6';
      }
      ctx.shadowBlur = state.isDragging ? 30 : 10;
      ctx.fill();
      ctx.shadowBlur = 0;
      if (!state.isDragging) {
        const pulseRadius = currentVariant === 'triangle' || currentVariant === 'square' ? 34 : 24;
        ctx.beginPath();
        ctx.arc(state.dotX, state.dotY, pulseRadius + Math.sin(Date.now() / 200) * 4, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(244, 114, 182, 0.5)';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      if (state.progressT >= VARIANTS[currentVariant].maxT) {
        updatePhase('straightening');
        state.straightenT = 0;
        setHumActive(false);
        const randomText = END_TEXTS[Math.floor(Math.random() * END_TEXTS.length)];
        setEndText(randomText);
        playStraightenSound();
        if ('vibrate' in navigator) navigator.vibrate([50, 50, 100]);
        playSuccess();
      }
    } else if (currentPhase === 'straightening') {
      state.straightenT += 0.015;
      if (state.straightenT >= 1) {
        state.straightenT = 1;
        updatePhase('done');
      }
      drawInterpolatedPath(ctx, currentVariant, state.straightenT, width, height);
    } else if (currentPhase === 'done') {
      drawInterpolatedPath(ctx, currentVariant, 1, width, height);
    }
    state.animationFrameId = requestAnimationFrame(gameLoop);
  }, [currentVariant, drawInterpolatedPath, getPathPoint, setHumActive, playStraightenSound, playSuccess, updatePhase]);
  // --- Interaction Handlers ---
  const handlePointerDown = useCallback((e: React.PointerEvent | React.TouchEvent | React.MouseEvent) => {
    if (isTheoryOpen) return;
    initAudio();
    if (phaseRef.current !== 'tracing') return;
    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }
    const state = stateRef.current;
    const margin = currentVariant === 'triangle' || currentVariant === 'square' ? 100 : 80;
    const dist = Math.hypot(clientX - state.dotX, clientY - state.dotY);
    if (dist < margin) {
      state.isDragging = true;
      setShowInstruction(false);
      if ('vibrate' in navigator) navigator.vibrate(10);
    }
    state.lastPointerX = state.pointerX = clientX;
    state.lastPointerY = state.pointerY = clientY;
  }, [isTheoryOpen, initAudio, currentVariant]);
  const handlePointerMove = useCallback((e: React.PointerEvent | React.TouchEvent | React.MouseEvent) => {
    if (isTheoryOpen) return;
    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }
    const state = stateRef.current;
    if (state.isDragging && phaseRef.current === 'tracing') {
      const dx = clientX - state.lastPointerX;
      const dy = clientY - state.lastPointerY;
      const vData = VARIANTS[currentVariant];
      const epsilon = vData.maxT * 0.01;
      const t1 = Math.max(0, state.progressT - epsilon);
      const t2 = Math.min(vData.maxT, state.progressT + epsilon);
      const width = canvasRef.current?.width || window.innerWidth;
      const height = canvasRef.current?.height || window.innerHeight;
      const pt1 = getPathPoint(currentVariant, t1, width, height);
      const pt2 = getPathPoint(currentVariant, t2, width, height);
      let tx = pt2.x - pt1.x;
      let ty = pt2.y - pt1.y;
      const len = Math.hypot(tx, ty);
      if (len > 0.001) {
        tx /= len;
        ty /= len;
        const movePixel = dx * tx + dy * ty;
        let resistance = 1.0;
        if (currentVariant === 'triangle') resistance = 0.35;
        if (currentVariant === 'square') resistance = 0.5;
        const increment = movePixel * ((t2 - t1) / len) * resistance;
        if (increment < -0.01 * resistance) {
          state.isWrongDirection = true;
          state.progressT += increment;
          setHumActive(false);
          const now = Date.now();
          if (now - state.lastVibrateTime > 150) {
            if ('vibrate' in navigator) navigator.vibrate(15);
            state.lastVibrateTime = now;
          }
        } else if (increment > 0.01 * resistance) {
          state.isWrongDirection = false;
          state.progressT += increment;
          setHumActive(true);
        } else {
          setHumActive(false);
        }
        state.progressT = Math.max(0, Math.min(state.progressT, vData.maxT));
        if (currentVariant === 'circles') {
          let currentLap = Math.floor(state.progressT / (Math.PI * 2));
          if (currentLap > state.lastLap && currentLap < 6) {
            playLapSound();
            state.lastLap = currentLap;
            if ('vibrate' in navigator) navigator.vibrate(30);
          }
        }
      }
    }
    state.pointerX = state.lastPointerX = clientX;
    state.pointerY = state.lastPointerY = clientY;
  }, [isTheoryOpen, currentVariant, getPathPoint, setHumActive, playLapSound]);
  const handlePointerUp = useCallback(() => {
    const state = stateRef.current;
    state.isDragging = false;
    setHumActive(false);
  }, [setHumActive]);
  // --- Setup & Teardown ---
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current) {
        canvasRef.current.width = window.innerWidth;
        canvasRef.current.height = window.innerHeight;
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    // Reset state for new variant
    const state = stateRef.current;
    state.progressT = 0;
    state.trail = [];
    state.lastTrailT = -1;
    updatePhase('tracing');
    state.isDragging = false;
    state.isWrongDirection = false;
    state.lastLap = 0;
    setHumActive(false);
    setShowInstruction(true);
    if (currentVariant === 'infinity') {
      const randomLoops = Math.floor(Math.random() * 5) + 3;
      VARIANTS.infinity.maxT = Math.PI * 2 * randomLoops;
    }
    state.animationFrameId = requestAnimationFrame(gameLoop);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(state.animationFrameId);
    };
  }, [currentVariant, gameLoop, setHumActive, updatePhase]);
  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      const state = stateRef.current;
      if (state.humOscillator) {
        try {
          state.humOscillator.stop();
        } catch (e) {}
      }
      if (state.audioCtx && state.audioCtx.state !== 'closed') {
        state.audioCtx.close().catch(() => {});
      }
      window.speechSynthesis.cancel();
    };
  }, []);
  const handleVariantChange = (type: VariantType) => {
    playClick();
    window.speechSynthesis.cancel();
    setIsTheoryOpen(false);
    setIsReadingTheory(false);
    setCurrentVariant(type);
    if ('vibrate' in navigator) navigator.vibrate(30);
  };
  const openTheory = () => {
    playClick();
    stateRef.current.isDragging = false;
    setIsTheoryOpen(true);
    // Speak theory
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(VARIANTS[currentVariant].theory);
    utterance.lang = 'pl-PL';
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.onstart = () => setIsReadingTheory(true);
    utterance.onend = () => {
      setIsReadingTheory(false);
      setTimeout(() => setIsTheoryOpen(false), 1000);
    };
    utterance.onerror = () => setIsReadingTheory(false);
    const voices = window.speechSynthesis.getVoices();
    const plVoice = voices.find(v => v.lang.startsWith('pl'));
    if (plVoice) utterance.voice = plVoice;
    window.speechSynthesis.speak(utterance);
  };
  const closeTheory = () => {
    playClick();
    window.speechSynthesis.cancel();
    setIsReadingTheory(false);
    setIsTheoryOpen(false);
  };
  return <div className="fixed inset-0 z-50 flex flex-col bg-slate-900 text-white overflow-hidden select-none touch-none" style={{
    background: 'linear-gradient(to bottom, #1e1b4b, #0f172a)'
  }} data-id="element-1114">
      
      {/* Background Nebulas */}
      <div className="absolute inset-0 z-0 opacity-30 pointer-events-none overflow-hidden" data-id="element-1115">
        <motion.div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600 rounded-full mix-blend-screen blur-[100px]" animate={{
        opacity: [0.6, 1, 0.6],
        filter: ['blur(80px)', 'blur(120px)', 'blur(80px)']
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-1116" />
        
        <motion.div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-600 rounded-full mix-blend-screen blur-[100px]" animate={{
        opacity: [0.6, 1, 0.6],
        filter: ['blur(80px)', 'blur(120px)', 'blur(80px)']
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 2
      }} data-id="element-1117" />
        
      </div>

      {/* Header */}
      <div className="w-full px-6 pt-12 pb-4 flex flex-col items-center z-20 pointer-events-none" data-id="element-1118">
        <div className="flex items-center justify-center space-x-4 mb-4 pointer-events-auto w-full relative" data-id="element-1119">
          {onClose && <button onClick={() => {
          playClick();
          onClose();
        }} className="absolute left-0 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white border border-white/20 backdrop-blur-md" data-id="element-1120">
            
              <X className="w-5 h-5" data-id="element-1121" />
            </button>}
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-pink-300 border border-white/20 shadow-[0_0_15px_rgba(244,114,182,0.2)]" data-id="element-1122">
            <Fingerprint className="w-6 h-6" data-id="element-1123" />
          </div>
          <button onClick={openTheory} className="bg-white/10 hover:bg-white/20 border border-white/20 text-white text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-full flex items-center transition-all shadow-lg backdrop-blur-sm group" data-id="element-1124">
            
            <PlayCircle className="w-5 h-5 mr-2 text-pink-400 group-hover:scale-110 transition-transform" data-id="element-1125" />
            Posłuchaj Teorii
          </button>
        </div>
        <h1 className="text-3xl font-light tracking-tight mb-2 text-center" data-id="element-1126">
          {VARIANTS[currentVariant].title}
        </h1>
        <h2 className="text-xs font-bold text-pink-300 uppercase tracking-widest mb-3 text-center" data-id="element-1127">
          {VARIANTS[currentVariant].subtitle}
        </h2>
        <p className="text-[11px] text-white/60 text-center max-w-sm leading-relaxed mb-4" data-id="element-1128">
          {VARIANTS[currentVariant].desc}
        </p>
      </div>

      {/* Instruction */}
      <AnimatePresence data-id="element-1129">
        {showInstruction && phase === 'tracing' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute top-[45%] w-full text-center z-20 pointer-events-none" data-id="element-1130">
          
            <p className="text-sm font-medium text-white/80 tracking-widest bg-black/40 inline-block px-6 py-2 rounded-full backdrop-blur-md border border-white/10" data-id="element-1131">
              Dotknij świetlistego punktu i podążaj za nim
            </p>
          </motion.div>}
      </AnimatePresence>

      {/* Canvas */}
      <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full z-10 touch-none" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} data-id="element-1132" />
      

      {/* End Text */}
      <AnimatePresence data-id="element-1133">
        {phase === 'done' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="absolute inset-0 flex flex-col items-center justify-center z-20 pointer-events-none pt-16" data-id="element-1134">
          
            <h2 className="text-3xl font-light text-white mb-16 tracking-wide text-center mt-10" data-id="element-1135">
              Harmonia przywrócona
            </h2>
            <p className="text-sm text-pink-200/80 tracking-widest uppercase text-center mt-12" data-id="element-1136">
              {endText}
            </p>

            <div className="mt-16 pointer-events-auto" data-id="element-1137">
              <button onClick={() => {
            playClick();
            onComplete();
          }} className="bg-white text-slate-900 rounded-full px-8 py-4 text-sm font-bold hover:bg-slate-100 transition-colors flex items-center justify-center space-x-2 shadow-lg" data-id="element-1138">
              
                <span data-id="element-1139">Przejdź dalej</span>
                <ArrowRight className="w-4 h-4" data-id="element-1140" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Bottom Menu - Horizontal Scroll */}
      {isStandalone && phase !== 'done' && <div className="mt-auto mb-8 w-full px-4 z-20 flex flex-col items-center pointer-events-auto" data-id="element-1141">
          <p className="text-[9px] text-white/40 uppercase tracking-widest mb-3" data-id="element-1142">
            Wybierz Moduł
          </p>
          <div className="flex gap-2 w-full max-w-md overflow-x-auto pb-2 scrollbar-hide" style={{
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }} data-id="element-1143">
          
            <VariantButton type="infinity" current={currentVariant} onClick={handleVariantChange} icon={<InfinityIcon className="w-5 h-5 mb-1" data-id="element-1145" />} label="EMDR" data-id="element-1144" />
          
            <VariantButton type="circles" current={currentVariant} onClick={handleVariantChange} icon={<RefreshCw className="w-5 h-5 mb-1" data-id="element-1147" />} label="Cykliczność" data-id="element-1146" />
          
            <VariantButton type="wave" current={currentVariant} onClick={handleVariantChange} icon={<Activity className="w-5 h-5 mb-1" data-id="element-1149" />} label="Somatyka" data-id="element-1148" />
          
            <VariantButton type="triangle" current={currentVariant} onClick={handleVariantChange} icon={<Triangle className="w-5 h-5 mb-1" data-id="element-1151" />} label="Uziom" data-id="element-1150" />
          
            <VariantButton type="square" current={currentVariant} onClick={handleVariantChange} icon={<Square className="w-5 h-5 mb-1" data-id="element-1153" />} label="Struktura" data-id="element-1152" />
          
            <VariantButton type="spiral" current={currentVariant} onClick={handleVariantChange} icon={<Target className="w-5 h-5 mb-1" data-id="element-1155" />} label="Centrum" data-id="element-1154" />
          
          </div>
        </div>}

      {/* Skip button for ritual mode */}
      {!isStandalone && phase !== 'done' && <div className="mt-auto mb-8 w-full px-4 z-20 flex flex-col items-center pointer-events-auto" data-id="element-1156">
          <button onClick={() => {
        playClick();
        onComplete();
      }} className="text-xs text-white/40 hover:text-white/70 transition-colors py-4" data-id="element-1157">
          
            Pomiń →
          </button>
        </div>}

      {/* Theory Modal */}
      <AnimatePresence data-id="element-1158">
        {isTheoryOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/60 backdrop-blur-md px-6 pointer-events-auto" data-id="element-1159">
          
            <motion.div initial={{
          scale: 0.95
        }} animate={{
          scale: 1
        }} exit={{
          scale: 0.95
        }} className="bg-[#1e1b4b] border border-pink-500/30 rounded-3xl w-full max-w-sm flex flex-col shadow-2xl relative overflow-hidden max-h-[80vh]" data-id="element-1160">
            
              <div className="absolute top-0 right-0 w-48 h-48 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-1161" />

              <div className="p-6 pb-2 flex justify-between items-start relative z-10" data-id="element-1162">
                <div className="flex items-center space-x-3" data-id="element-1163">
                  <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center border border-pink-500/30 shadow-sm text-pink-300" data-id="element-1164">
                    <Fingerprint className="w-5 h-5" data-id="element-1165" />
                  </div>
                  <div data-id="element-1166">
                    <p className="text-[10px] text-pink-300 uppercase tracking-widest font-bold" data-id="element-1167">
                      Wiedza
                    </p>
                    <h3 className="text-lg font-bold text-white leading-tight" data-id="element-1168">
                      Teoria Modułu
                    </h3>
                  </div>
                </div>
                <button onClick={closeTheory} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/20 transition-colors" data-id="element-1169">
                
                  <X className="w-4 h-4" data-id="element-1170" />
                </button>
              </div>

              <div className="p-6 relative z-10 flex-1 overflow-y-auto" data-id="element-1171">
                <p className="text-sm text-white/80 leading-relaxed italic border-l-2 border-pink-500/50 pl-4" data-id="element-1172">
                  {VARIANTS[currentVariant].theory}
                </p>
              </div>

              <div className="p-6 pt-2 border-t border-white/10 flex justify-between items-center relative z-10 bg-black/20" data-id="element-1173">
                <div className="flex items-center space-x-2" style={{
              opacity: isReadingTheory ? 1 : 0.3
            }} data-id="element-1174">
                
                  <motion.div className="w-1 bg-pink-400 rounded-full" animate={{
                height: isReadingTheory ? [12, 24, 12] : 12
              }} transition={{
                duration: 0.5,
                repeat: Infinity
              }} data-id="element-1175" />
                
                  <motion.div className="w-1 bg-pink-400 rounded-full" animate={{
                height: isReadingTheory ? [20, 10, 20] : 20
              }} transition={{
                duration: 0.6,
                repeat: Infinity
              }} data-id="element-1176" />
                
                  <motion.div className="w-1 bg-pink-400 rounded-full" animate={{
                height: isReadingTheory ? [16, 28, 16] : 16
              }} transition={{
                duration: 0.4,
                repeat: Infinity
              }} data-id="element-1177" />
                
                  <motion.div className="w-1 bg-pink-400 rounded-full" animate={{
                height: isReadingTheory ? [24, 12, 24] : 24
              }} transition={{
                duration: 0.7,
                repeat: Infinity
              }} data-id="element-1178" />
                
                </div>
                <button onClick={closeTheory} className="text-xs font-bold text-pink-300 uppercase tracking-widest hover:text-pink-100 transition-colors" data-id="element-1179">
                
                  Zatrzymaj
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function VariantButton({
  type,
  current,
  onClick,
  icon,
  label
}: {
  type: VariantType;
  current: VariantType;
  onClick: (t: VariantType) => void;
  icon: React.ReactNode;
  label: string;
}) {
  const isActive = type === current;
  return <button onClick={() => onClick(type)} className={`py-3 px-5 rounded-2xl flex flex-col items-center justify-center transition-colors border flex-shrink-0 min-w-[80px] ${isActive ? 'bg-pink-500/15 border-pink-400/40 text-pink-200' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} style={{
    backdropFilter: 'blur(10px)'
  }} data-id="element-1180">
      
      {icon}
      <span className="text-[9px] font-bold uppercase whitespace-nowrap" data-id="element-1181">
        {label}
      </span>
    </button>;
}